import { ToastRegion } from '@a6tr/components/ToastRegion/ToastRegion';
import { TransportProvider } from '@connectrpc/connect-query';
import * as Sentry from '@sentry/react';
import {
	MutationCache,
	QueryCache,
	QueryClient,
	QueryClientProvider,
} from '@tanstack/react-query';
import { createRouter, RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

import { handleApiError } from './config/apiErrorHandler.ts';
import { getTransport } from './config/transport.ts';
import './i18n/i18n.ts';
import { initSentry } from './lib/sentry.ts';

import './main.css';
import { routeTree } from './routeTree.gen';

const queryClient = new QueryClient({
	mutationCache: new MutationCache({
		onError(error, _variables, _context, mutation) {
			handleApiError(error, mutation.options.meta?.errorMessage);
		},
	}),
	queryCache: new QueryCache({
		onError(error, query) {
			handleApiError(error, query.options.meta?.errorMessage, {
				toast: !query.options.meta?.noErrorToast,
			});
		},
	}),
	defaultOptions: {
		queries: {
			refetchOnWindowFocus: false,
			retry: 1,
		},
	},
});

declare module '@tanstack/react-router' {
	interface Register {
		router: typeof router;
	}
}

const router = createRouter({ routeTree });

initSentry(router);

const container = document.getElementById('root');
if (!container) {
	throw new Error('Root element not found');
}

createRoot(container, {
	onUncaughtError: Sentry.reactErrorHandler(),
	onCaughtError: Sentry.reactErrorHandler(),
	onRecoverableError: Sentry.reactErrorHandler(),
}).render(
	<StrictMode>
		<TransportProvider transport={getTransport()}>
			<QueryClientProvider client={queryClient}>
				<ToastRegion />
				<RouterProvider router={router} />
			</QueryClientProvider>
		</TransportProvider>
	</StrictMode>,
);
